import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { api, waLink } from "../lib/api";
import { useConfig } from "../lib/config";
import { Seo } from "../components/site/Seo";
import { ProductCard } from "../components/site/ProductCard";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "../components/ui/breadcrumb";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const config = useConfig();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="py-32 text-center" data-testid="product-not-found">
        <h1 className="text-2xl font-black text-zinc-950">Producto no encontrado</h1>
        <Link to="/#productos" className="text-brand-700 font-bold text-sm underline mt-4 inline-block">Volver al catálogo</Link>
      </main>
    );
  }

  if (!product) {
    return <main className="py-32 text-center text-sm text-zinc-500" data-testid="product-loading">Cargando producto…</main>;
  }

  const paragraphs = product.description.split("\n").filter(Boolean);
  const bullets = paragraphs.filter((l) => l.trim().startsWith("- "));
  const intro = paragraphs.filter((l) => !l.trim().startsWith("- "));

  const wa = waLink(config.whatsapp_number || "", `Hola, me interesa el producto ${product.name}. ¿Dónde puedo comprarlo?`);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.image ? [product.image] : undefined,
    description: intro.join(" ") || product.name,
    brand: { "@type": "Brand", name: "AZOO" },
    category: product.category,
  };

  return (
    <main className="bg-white" data-testid="product-detail-page">
      <Seo
        title={`${product.name} — AZOO Costa Rica`}
        description={`${product.name} (${product.presentation || product.line}). ${product.category} AZOO distribuido en Costa Rica por Tugeko S.A. Consulta dónde comprar por WhatsApp.`}
        jsonLd={jsonLd}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Breadcrumb data-testid="product-breadcrumb">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/">Inicio</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/#productos">Productos</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="max-w-[200px] truncate">{product.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="aspect-square bg-zinc-50 rounded-3xl border border-zinc-200 flex items-center justify-center p-8" data-testid="product-image-wrapper">
          {product.image ? (
            <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain" data-testid="product-image" />
          ) : (
            <span className="text-zinc-300">Sin imagen</span>
          )}
        </div>

        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            <span className="bg-brand-600 text-white text-[11px] font-bold px-2.5 py-1 rounded" data-testid="product-category-badge">{product.category}</span>
            {product.line && <span className="bg-zinc-950 text-white text-[11px] font-bold px-2.5 py-1 rounded" data-testid="product-line-badge">{product.line}</span>}
            {product.presentation && <span className="bg-zinc-100 text-zinc-700 text-[11px] font-bold px-2.5 py-1 rounded border border-zinc-200" data-testid="product-presentation-badge">{product.presentation}</span>}
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-zinc-950 leading-tight" data-testid="product-name">{product.name}</h1>

          <div className="space-y-4" data-testid="product-description">
            {intro.map((p, i) => (
              <p key={i} className="text-sm text-zinc-600 leading-relaxed">{p}</p>
            ))}
            {bullets.length > 0 && (
              <ul className="space-y-2">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0"></span>
                    {b.trim().slice(2)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-brand-500 hover:bg-brand-400 text-zinc-950 font-black text-sm uppercase tracking-wider transition-colors shadow-lg hover:shadow-brand-500/25"
            data-testid="product-whatsapp-button"
          >
            <MessageCircle className="w-4 h-4" /> Consultar dónde comprar
          </a>
          <p className="text-xs text-zinc-500">Te indicamos el punto de venta más cercano en Costa Rica.</p>
        </div>
      </section>

      {product.related && product.related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="related-products">
          <h2 className="text-xl font-black text-zinc-950 mb-8">También en {product.category}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {product.related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
